import React from "react";

import MUICard from "@mui/material/Card";
import MUICardContent from "@mui/material/CardContent";
import MUICardHeader from "@mui/material/CardHeader";
import MUIStack from "@mui/material/Stack";

/**
 * Renders the main content section for the layout. Override by passing
 * renderContent prop.
 * @returns {React.ReactNode|null}
 */
function defaultRenderContent() {
  return null;
}

/**
 * @returns {string} The title of the card. Defaults to empty; subclasses
 * should override.
 */
function defaultGetTitle() {
  return null;
}

/**
 * @returns {string} The icon of the card. Defaults to empty; subclasses
 * should override.
 */
function defaultGetIcon() {
  return null;
}

/**
 * @returns {array} The MUIMenuItems/buttons/etc for the top right of the
 * card. Defaults to empty; subclasses should override.
 */
function defaultGetActions() {
  return null;
}

/**
 * @returns {string} the background color of the card. This can be anything
 * you can put in the CSS "background" attribute. Defaults to white.
 */
function defaultGetBackground() {
  return null;
}

/**
 * @returns {string} the minimum height of the card. Override if necessary;
 * defaults to empty. Most common to override when cards are used in a grid and
 * we need cards to match the same height (especially on page resize).
 */
function defaultGetMinHeight() {
  return null;
}

/**
 * @returns {number} The padding of the card. Override if necessary;
 * defaults to 2 (16px). Most common situation is to override to 0 for a
 * "full-bleed" look.
 */
function defaultGetPadding() {
  return 2;
}

/**
 * @returns {number} The margin of the card. Override if necessary;
 * defaults to 2 (16px). Most common situation is to override to 0 for a
 * "full-bleed" look.
 */
function defaultGetMarginBottom() {
  return 2;
}

/**
 * @returns {boolean} Whether the card should fill height. Defaults to false.
 */
function defaultContentFillHeight() {
  return false;
}

export default function Card({
  renderContent = defaultRenderContent,
  getTitle = defaultGetTitle,
  getIcon = defaultGetIcon,
  getMarginBottom = defaultGetMarginBottom,
  getBackground = defaultGetBackground,
  getMinHeight = defaultGetMinHeight,
  getPadding = defaultGetPadding,
  getActions = defaultGetActions,
  getContentFillHeight = defaultContentFillHeight,
}) {
  return (
    <MUICard
      elevation={2}
      sx={{
        width: "100%", // Always fill parent width by default
        marginBottom: getMarginBottom(),
        background: getBackground(),
        minHeight: getMinHeight(),
        display: getContentFillHeight() ? "flex" : undefined,
        flexDirection: getContentFillHeight() ? "column" : undefined,
        containerType: "inline-size",
      }}
    >
      <MUICardHeader
        action={(() => {
          const actions = getActions();
          if (actions !== null) {
            return (
              <MUIStack direction="row" spacing={1}>
                {actions.map((action, i) =>
                  React.cloneElement(action, { key: i }),
                )}
              </MUIStack>
            );
          }
          return null;
        })()}
        title={(() => {
          const Icon = getIcon();
          const title = getTitle();
          if (Icon) {
            return (
              <MUIStack
                direction="row"
                spacing={0}
                sx={{
                  alignItems: "center",
                }}
              >
                <Icon
                  fontSize="small"
                  color="primary"
                  sx={{ marginRight: 1 }}
                />
                <span>{title}</span>
              </MUIStack>
            );
          }
          return title;
        })()}
        sx={{ paddingBottom: 0 }}
        slotProps={{
          title: {
            fontSize: 18,
            // fontWeight: "medium", //Uncomment this if Kenny says title's need to be bolder
            color: "text.secondary",
            fontWeight: "400",
          },
        }}
      />
      <MUICardContent
        sx={{
          padding: getPadding(),
          flex: getContentFillHeight() ? 1 : undefined,
          display: getContentFillHeight() ? "flex" : undefined,
          flexDirection: getContentFillHeight() ? "column" : undefined,
          overflow: getContentFillHeight() ? "auto" : undefined,
          maxHeight: getContentFillHeight() ? "100%" : undefined,
        }}
      >
        {renderContent()}
      </MUICardContent>
    </MUICard>
  );
}
